import {
  BLAST_TICKS,
  BOMB_FUSE_TICKS,
  Curse,
  DIR_VECTORS,
  FULL_FIRE_POWER,
  Item,
  TICK_HZ,
  Tile,
} from './constants';
import { hazardArming, hazardHarms, hazardLive, hazardSpeed } from './hazards';
import type { Sim } from './sim';
import { bombAt, idxOf, inBounds, tileAt, flyingBombAt } from './sim';
import { RoomGoal, type GameState, type Input, type PlayerState } from './types';

/**
 * Bot difficulty tuning. Higher tiers think more often, flee earlier and are
 * more willing to take fights.
 */
export interface BotProfile {
  name: string;
  /** Ticks between full re-plans. */
  thinkInterval: number;
  /** Probability of a deliberately wasted decision. */
  sloppiness: number;
  /** How far the bot will path to chase an enemy. */
  huntRange: number;
  /** How far the bot will detour for a pickup. */
  itemRange: number;
  /** Extra ticks of safety margin demanded before entering a cell. */
  caution: number;
  /** Chance per plan of laying a bomb when an opportunity exists. */
  aggression: number;
  /** Whether the bot understands kicking and punching. */
  usesGear: boolean;
  /** Seconds the bot waits after laying a bomb before considering another. */
  bombCooldown: number;
}

export const BOT_PROFILES: BotProfile[] = [
  {
    name: 'Rookie',
    bombCooldown: 1.2,
    thinkInterval: 9,
    sloppiness: 0.3,
    // Rookies do engage -- they are beatable because they react slowly and
    // make deliberate mistakes, not because they stand around doing nothing.
    huntRange: 4,
    itemRange: 5,
    caution: 10,
    aggression: 0.6,
    usesGear: false,
  },
  {
    name: 'Fighter',
    bombCooldown: 1.1,
    thinkInterval: 5,
    sloppiness: 0.12,
    huntRange: 7,
    itemRange: 9,
    caution: 6,
    aggression: 0.7,
    usesGear: false,
  },
  {
    name: 'Veteran',
    bombCooldown: 0.85,
    thinkInterval: 3,
    sloppiness: 0.04,
    huntRange: 12,
    itemRange: 14,
    caution: 5,
    aggression: 0.85,
    usesGear: true,
  },
  {
    name: 'Nightmare',
    bombCooldown: 0.8,
    thinkInterval: 2,
    sloppiness: 0,
    // Deliberately bounded. An unbounded hunt sends the bot on long treks
    // across a board full of other people's bombs; staying local, farming
    // power-ups and taking guaranteed kills beats chasing every time.
    huntRange: 20,
    itemRange: 16,
    caution: 6,
    aggression: 0.92,
    usesGear: true,
  },
];

const INF = 0x7fffffff;

/**
 * What a wire about to go live costs a plan.
 *
 * Roughly its warning window, so the planner treats it the way it treats a
 * fuse with that long left: crossable if you are quick, not somewhere to stand.
 */
const HAZARD_ARM_TICKS = Math.round(0.7 * TICK_HZ);

/**
 * Ticks until each cell becomes lethal, or `INF` if it is currently safe.
 * Accounts for chained detonations by relaxing a few passes.
 *
 * `viewerId` matters only for remote bombs, which have no fuse at all. Somebody
 * else's detonator can be pressed at any moment, so theirs read as imminent.
 * Your own cannot surprise you — you are the one holding the button — so they
 * read as an ordinary fuse. Without that distinction a bot is trapped by its
 * own bomb the instant it lays one: every neighbouring cell is permanently
 * about to catch fire, no escape route ever passes the time check, and the bot
 * stands on its own bomb until the round ends.
 */
export function computeDanger(state: GameState, viewerId?: string): Int32Array {
  const n = state.width * state.height;
  const danger = new Int32Array(n).fill(INF);

  for (const blast of state.blasts) {
    for (const idx of blast.cells) danger[idx] = 0;
  }

  /*
   * A creature's tile is lethal ground, same as fire.
   *
   * Contact damage is the only attack they have, so from a planner's point of
   * view standing where one is standing costs a hit -- which is exactly what
   * `danger` already means. Marked as 0 rather than a countdown because it is
   * not on a fuse: it is dangerous now.
   *
   * The tile it is *about* to be on is not marked. That would be predicting a
   * movement rule the planner does not model, and creatures are slower than
   * every player, so walking away is always available.
   *
   * Empty outside Expedition, so nothing about deathmatch changes.
   */
  for (const e of state.enemies) {
    if (!e.alive) continue;
    danger[idxOf(state, Math.floor(e.x), Math.floor(e.y))] = 0;
  }

  /*
   * A live wire is lethal ground, and one about to go live is nearly so.
   *
   * The arming window is the important half. Without it a bot walks onto a
   * dead wire that comes up under it, which is not a mistake the bot made --
   * the information was on screen and the planner could not see it. Costed at
   * the warning length rather than 0 so it reads as "somewhere you have to be
   * out of shortly", which is exactly what `danger` already means for a fuse.
   *
   * Only the harmful kinds. Ice and undergrowth change how a bot moves, and
   * the planner walks them without knowing; a fan pushes it about, which is a
   * real gap and a much smaller one than being electrocuted.
   */
  for (let cell = 0; cell < state.hazards.length; cell++) {
    const kind = state.hazards[cell];
    if (!hazardHarms(kind)) continue;
    if (hazardLive(kind, cell, state.tick)) danger[cell] = 0;
    else if (hazardArming(kind, cell, state.tick)) {
      danger[cell] = Math.min(danger[cell], HAZARD_ARM_TICKS);
    }
  }

  if (!state.bombs.length) return danger;

  const fuses = state.bombs.map((b) => {
    if (b.fuse !== Number.MAX_SAFE_INTEGER) return Math.max(0, b.fuse);
    return viewerId !== undefined && b.ownerId === viewerId ? BOMB_FUSE_TICKS : Math.round(0.4 * TICK_HZ);
  });

  // Relax chains: a bomb inside another's blast inherits the earlier fuse.
  for (let pass = 0; pass < 4; pass++) {
    let changed = false;
    for (let i = 0; i < state.bombs.length; i++) {
      const covered = blastCells(state, state.bombs[i]);
      for (let j = 0; j < state.bombs.length; j++) {
        if (i === j) continue;
        const other = state.bombs[j];
        const idx = idxOf(state, Math.floor(other.x), Math.floor(other.y));
        if (covered.includes(idx) && fuses[i] < fuses[j]) {
          fuses[j] = fuses[i];
          changed = true;
        }
      }
    }
    if (!changed) break;
  }

  for (let i = 0; i < state.bombs.length; i++) {
    for (const idx of blastCells(state, state.bombs[i])) {
      if (fuses[i] < danger[idx]) danger[idx] = fuses[i];
    }
  }
  return danger;
}

/** Flat cell indices a bomb would cover if it detonated right now. */
export function blastCells(state: GameState, bomb: { x: number; y: number; power: number; pierce: boolean }): number[] {
  const cx = Math.floor(bomb.x);
  const cy = Math.floor(bomb.y);
  const out = [idxOf(state, cx, cy)];
  for (let d = 0; d < 4; d++) {
    const [dx, dy] = DIR_VECTORS[d];
    for (let r = 1; r <= bomb.power; r++) {
      const x = cx + dx * r;
      const y = cy + dy * r;
      if (!inBounds(state, x, y)) break;
      const tile = tileAt(state, x, y);
      if (tile === Tile.HARD) break;
      out.push(idxOf(state, x, y));
      if (tile === Tile.SOFT) {
        if (!bomb.pierce) break;
        continue;
      }
    }
  }
  return out;
}

interface BfsResult {
  dist: Int32Array;
  prev: Int32Array;
}

/**
 * Time-aware flood fill from a cell. A cell is only entered if the bot can
 * reach it before it becomes lethal.
 */
function bfs(
  state: GameState,
  start: number,
  danger: Int32Array,
  ticksPerTile: number,
  caution: number,
  passSoft: boolean,
  /**
   * When true (the normal case) a tile is only entered if the bot can also
   * *leave* it before it catches fire. Fleeing bots set this false: crossing a
   * tile that is about to blow is worth it if the alternative is standing in
   * one that already has.
   */
  requireExit = true,
): BfsResult {
  const n = state.width * state.height;
  const dist = new Int32Array(n).fill(INF);
  const prev = new Int32Array(n).fill(-1);
  const queue: number[] = [start];
  dist[start] = 0;
  let head = 0;

  while (head < queue.length) {
    const cur = queue[head++];
    const cx = cur % state.width;
    const cy = (cur / state.width) | 0;
    const arrive = dist[cur] * ticksPerTile;

    for (let d = 0; d < 4; d++) {
      const [dx, dy] = DIR_VECTORS[d];
      const nx = cx + dx;
      const ny = cy + dy;
      if (!inBounds(state, nx, ny)) continue;
      const idx = idxOf(state, nx, ny);
      if (dist[idx] !== INF) continue;
      const tile = state.grid[idx];
      if (tile === Tile.HARD) continue;
      if (tile === Tile.SOFT && !passSoft) continue;
      if (bombAt(state, nx, ny)) continue;
      const nextArrive = arrive + ticksPerTile;
      // Refuse to walk into fire we cannot clear in time. `clearBy` is when we
      // would be out the far side, which is the number that actually matters:
      // entering a tile 1 tick before it detonates is fatal even though we
      // "arrived in time".
      const d0 = danger[idx];
      const clearBy = nextArrive + caution + (requireExit ? ticksPerTile : 0);
      if (d0 !== INF && d0 <= clearBy && nextArrive <= d0 + BLAST_TICKS) continue;
      dist[idx] = dist[cur] + 1;
      prev[idx] = cur;
      queue.push(idx);
    }
  }
  return { dist, prev };
}

/** Per-bot scratch state carried between ticks. */
export interface BotMemory {
  cooldown: number;
  plan: number[];
  bombCooldown: number;
  /** Cell the bot is currently running to, so a flee does not thrash. */
  fleeTarget: number;
  wanderDir: number;
  wanderTicks: number;
  /** Ticks the bot has been sitting on an armed remote bomb. */
  remoteHold: number;
}

export function createBotMemory(): BotMemory {
  return {
    cooldown: 0,
    plan: [],
    bombCooldown: 0,
    fleeTarget: -1,
    wanderDir: 0,
    wanderTicks: 0,
    remoteHold: 0,
  };
}

/**
 * Produces one tick of input for a bot. Pure with respect to the simulation —
 * it reads state and returns intent, exactly like a human controller.
 */
/**
 * An optional witness for which decision a bot took.
 *
 * Off by default and a null check when it is: this runs a few thousand times a
 * second and must cost nothing in a match. `tools/mechanics-test.ts` turns it
 * on to check that every branch is reachable at all, which is the one thing
 * nothing else here can see. The punch behaviour sat behind an early return
 * for its whole life -- it fired once in twenty-four matches, and no test,
 * probe or measurement noticed, because a branch that never runs looks exactly
 * like a mechanic that does not matter.
 */
let trace: ((tier: string, branch: string) => void) | null = null;

export function setBotTrace(fn: ((tier: string, branch: string) => void) | null): void {
  trace = fn;
}

export function botInput(sim: Sim, bot: PlayerState, mem: BotMemory, rand: () => number): Input {
  const state = sim.state;
  const profile = BOT_PROFILES[Math.min(BOT_PROFILES.length - 1, Math.max(0, bot.botLevel))];
  const out: Input = { mx: 0, my: 0, bomb: false, action: false };
  if (!bot.alive || state.phase === 'over' || state.phase === 'countdown') return out;
  const mark = (branch: string) => trace?.(profile.name, branch);

  if (mem.bombCooldown > 0) mem.bombCooldown--;

  const speed = (3.6 + bot.speedLevel * 0.55) * hazardSpeed(state.hazards[idxOf(state, Math.floor(bot.x), Math.floor(bot.y))]);
  const ticksPerTile = Math.max(1, Math.round(TICK_HZ / speed));
  const cx = Math.floor(bot.x);
  const cy = Math.floor(bot.y);
  const here = idxOf(state, cx, cy);
  const danger = computeDanger(state, bot.id);
  const inPeril = danger[here] !== INF;

  // The detonator is checked every tick, not just on re-plan ticks, and it is
  // merged into whatever the bot was already doing rather than replacing it:
  // pressing the button costs no movement, so there is no reason to stand still
  // for it. Only `turn` has to pre-empt the tick, and only until the bot is no
  // longer nose-to-nose with a bomb.
  const trigger = considerDetonator(state, bot, mem, profile, here);
  if (trigger === 'turn') {
    mark('detonator-turn');
    return faceAwayFromBomb(state, bot, out);
  }
  if (trigger === 'fire') {
    mark('detonator-fire');
    out.action = true;
  }

  // Re-plan on a timer, but never keep walking a stale path into a fire that
  // was lit after the path was made.
  if (mem.cooldown > 0 && !inPeril && mem.plan.length && planStillSafe(mem, danger, ticksPerTile, profile.caution)) {
    mem.cooldown--;
    mark('follow-plan');
    return followPlan(state, bot, mem, out);
  }
  mem.cooldown = profile.thinkInterval;
  mem.plan = [];

  /*
   * Punch whatever is in the way, before deciding what else to do.
   *
   * This lived below the survive branch and could never run: a bomb in the
   * tile in front of you is a bomb whose blast covers the tile you are
   * standing in, so `inPeril` is true and step 1 returned first -- every time.
   * Over 24 matches the bots held the glove for 84% of player-ticks and had an
   * enemy bomb directly in front for 5,656 of them, and punched once. The
   * behaviour was not rare, it was unreachable, and nothing noticed for as
   * long as it existed.
   *
   * Above the branch rather than inside it, so it covers the calm case too --
   * a bomb flying past that is not yet a threat. It costs no movement, so it
   * is merged into whatever happens next rather than replacing it: the bot
   * punches the bomb away and still runs, which is what a person does.
   */
  if (!out.action && profile.usesGear && bot.punch) {
    const [fx, fy] = DIR_VECTORS[bot.dir];
    const grounded = bombAt(state, cx + fx, cy + fy);
    const incoming =
      state.settings.deflect === true ? flyingBombAt(state, cx + fx, cy + fy) : null;
    const target = grounded ?? (incoming && incoming.fuse > 0 ? incoming : null);
    if (target && target.ownerId !== bot.id) {
      mark('punch');
      out.action = true;
    }
  }

  /* 1. Survive. ------------------------------------------------------- */
  if (inPeril) {
    const walk = bfs(state, here, danger, ticksPerTile, Math.floor(profile.caution / 2), false, false);
    // Stick with the escape we already committed to while it is still valid.
    // Re-deciding every tick makes bots dither in the middle of a blast.
    if (mem.fleeTarget >= 0 && danger[mem.fleeTarget] === INF && walk.dist[mem.fleeTarget] !== INF) {
      mem.plan = pathTo(walk.prev, here, mem.fleeTarget);
      if (mem.plan.length) {
        mark('survive-committed');
        return followPlan(state, bot, mem, out);
      }
    }
    const target = bestRefuge(state, walk.dist, danger);
    if (target >= 0 && target !== here) {
      mem.fleeTarget = target;
      mem.plan = pathTo(walk.prev, here, target);
      mark('survive-refuge');
      return followPlan(state, bot, mem, out);
    }
    // No fully safe tile: take the cell that buys the most time.
    let best = -1;
    let bestScore = danger[here];
    for (let idx = 0; idx < walk.dist.length; idx++) {
      if (walk.dist[idx] === INF || idx === here) continue;
      if (danger[idx] > bestScore) {
        bestScore = danger[idx];
        best = idx;
      }
    }
    if (best >= 0) {
      mem.fleeTarget = -1;
      mem.plan = pathTo(walk.prev, here, best);
      mark('survive-least-bad');
      return followPlan(state, bot, mem, out);
    }
    mark('survive-cornered');
    return out;
  }
  mem.fleeTarget = -1;

  const walk = bfs(state, here, danger, ticksPerTile, profile.caution, false);

  /* 2. Deliberate mistakes at low difficulty. -------------------------- */
  if (profile.sloppiness > 0 && rand() < profile.sloppiness) {
    mark('sloppy');
    return wander(state, bot, mem, danger, ticksPerTile, rand, out);
  }

  if (state.settings.roomGoal === RoomGoal.EXIT && state.exitAt >= 0) {
    if (walk.dist[state.exitAt] !== INF) {
      mem.plan = pathTo(walk.prev, here, state.exitAt);
      mark('exit');
      return followPlan(state, bot, mem, out);
    }
    // If crates block the exit, open the first crate on a route to it.
    const tunnel = bfs(state, here, danger, ticksPerTile, profile.caution, true);
    const route = pathTo(tunnel.prev, here, state.exitAt);
    const blocked = route.findIndex(cell => state.grid[cell] === Tile.SOFT);
    if (blocked > 0) {
      mem.plan = route.slice(0, blocked);
      mark('exit-approach');
      return followPlan(state, bot, mem, out);
    }
    if (blocked === 0 && mem.bombCooldown === 0 && bot.bombsActive < bot.maxBombs &&
      hasEscape(state, bot, here, danger, ticksPerTile, profile)) {
      out.bomb = true;
      mem.bombCooldown = Math.round(profile.bombCooldown * TICK_HZ);
      mark('exit-open');
      return out;
    }
  }

  if (state.settings.roomGoal === RoomGoal.SURVIVE) {
    // Keep distance from contact damage while the clock advances. No need to
    // attack or collect gear to satisfy this room's objective.
    const safety = (cell: number) => state.enemies.filter(e => e.alive).reduce((d, e) =>
      Math.min(d, Math.abs(cell % state.width + 0.5 - e.x) + Math.abs(Math.floor(cell / state.width) + 0.5 - e.y)), 20);
    let target = here, score = safety(here);
    for (let cell = 0; cell < walk.dist.length; cell++) {
      if (walk.dist[cell] === INF || walk.dist[cell] > 5 || danger[cell] !== INF) continue;
      const value = safety(cell) - walk.dist[cell] * 0.15;
      if (value > score + 0.5) { score = value; target = cell; }
    }
    mem.plan = pathTo(walk.prev, here, target);
    mark('wait-safe');
    return followPlan(state, bot, mem, out);
  }

  if (state.settings.roomGoal === RoomGoal.HUNT) {
    const shot = evaluateBomb(state, bot, here, profile, ticksPerTile, danger);
    if (shot.value >= 5 && mem.bombCooldown === 0 && bot.bombsActive < bot.maxBombs &&
      hasEscape(state, bot, here, danger, ticksPerTile, profile)) {
      out.bomb = true;
      mem.bombCooldown = Math.round(profile.bombCooldown * TICK_HZ);
      mark('hunt-shot');
      return out;
    }
    const target = creatureTarget(state, walk.dist, danger, true);
    if (target >= 0) {
      mem.plan = pathTo(walk.prev, here, target);
      mark('hunt-quarry');
      return followPlan(state, bot, mem, out);
    }
  }

  /* 4. Snap up anything within arm's reach before committing to a bomb. -- */
  const closeItem = nearestWhere(
    walk.dist,
    (idx) => state.items[idx] !== Item.NONE && danger[idx] === INF && walk.dist[idx] > 0 && walk.dist[idx] <= 2,
  );
  if (closeItem >= 0) {
    mem.plan = pathTo(walk.prev, here, closeItem);
    mark('grab-close');
    return followPlan(state, bot, mem, out);
  }

  /* 5. Take a shot when it is safe to do so. ---------------------------- */
  const canBomb = mem.bombCooldown === 0 && bot.bombsActive < bot.maxBombs;
  if (canBomb) {
    const shot = evaluateBomb(state, bot, here, profile, ticksPerTile, danger);
    // A guaranteed kill is always worth taking, however timid the tier.
    const willing = shot.lethal || rand() < profile.aggression;
    /*
     * Hold an Overload for something worth it.
     *
     * The charge is one bomb, and the measurement that prompted this showed
     * bots spending it the instant it arrived -- on whatever wall they were
     * going to bomb anyway -- because nothing in here knew it was special. A
     * mechanic whose whole value is deliberate use, used indiscriminately,
     * measures as worthless whether or not it is.
     *
     * The bar is a kill or a shot several blocks wide. Gear-using tiers hold
     * out for more, which is the same axis every other tier difference runs
     * on: a Rookie takes the first decent option, a Nightmare waits for the
     * right one. Never an indefinite hold -- with no bar met, the bot bombs
     * as usual and simply spends it, because a bot standing still guarding a
     * power-up is worse than one that used it early.
     */
    const bar = bot.charged ? (profile.usesGear ? 4 : 3) : 0;
    const worthIt = shot.lethal || shot.value >= bar;
    if (shot.value > 0 && willing && worthIt && hasEscape(state, bot, here, danger, ticksPerTile, profile)) {
      mark('bomb');
      out.bomb = true;
      // Long enough that the bot always clears its own blast before it can
      // even consider laying the next one.
      mem.bombCooldown = Math.round(profile.bombCooldown * TICK_HZ);
      mem.cooldown = 0;
      mem.plan = [];
      return out;
    }
  }

  /* 6. Grab a nearby pickup. -------------------------------------------- */
  const item = nearestWhere(
    walk.dist,
    (idx) => state.items[idx] !== Item.NONE && danger[idx] === INF && walk.dist[idx] <= profile.itemRange,
  );
  if (item >= 0 && item !== here) {
    mem.plan = pathTo(walk.prev, here, item);
    mark('pickup');
    return followPlan(state, bot, mem, out);
  }

  /* 7. Hunt. ------------------------------------------------------------ */
  if (profile.huntRange > 0) {
    let bestEnemy = -1;
    let bestDist = INF;
    for (const p of state.players) {
      if (!p.alive || p.id === bot.id) continue;
      if (state.settings.mode === 'teams' && p.team === bot.team) continue;
      const idx = idxOf(state, Math.floor(p.x), Math.floor(p.y));
      // Path adjacent to the enemy rather than into them.
      for (let d = 0; d < 4; d++) {
        const [dx, dy] = DIR_VECTORS[d];
        const nIdx = idx + dy * state.width + dx;
        if (nIdx < 0 || nIdx >= walk.dist.length) continue;
        if (danger[nIdx] === INF && walk.dist[nIdx] < bestDist && walk.dist[nIdx] <= profile.huntRange) {
          bestDist = walk.dist[nIdx];
          bestEnemy = nIdx;
        }
      }
      if (danger[idx] === INF && walk.dist[idx] < bestDist && walk.dist[idx] <= profile.huntRange) {
        bestDist = walk.dist[idx];
        bestEnemy = idx;
      }
    }
    if (bestEnemy >= 0 && bestEnemy !== here) {
      mem.plan = pathTo(walk.prev, here, bestEnemy);
      mark('hunt');
      return followPlan(state, bot, mem, out);
    }
  }

  const creature = creatureTarget(state, walk.dist, danger, state.settings.roomGoal === RoomGoal.HUNT);
  if (creature >= 0) {
    mem.plan = pathTo(walk.prev, here, creature);
    mark('stalk');
    return followPlan(state, bot, mem, out);
  }

  /* 8. Otherwise open up the map. --------------------------------------- */
  const digSpot = nearestWhere(walk.dist, (idx) => danger[idx] === INF && adjacentSoft(state, idx));
  if (digSpot >= 0 && digSpot !== here) {
    mem.plan = pathTo(walk.prev, here, digSpot);
    mark('open-map');
    return followPlan(state, bot, mem, out);
  }

  // Nothing worth doing: drift so bots never look frozen.
  mark('wander-idle');
  return wander(state, bot, mem, danger, ticksPerTile, rand, out);
}

function creatureTarget(state: GameState, dist: Int32Array, danger: Int32Array, huntedOnly: boolean): number {
  let target = -1, nearest = INF;
  for (const e of state.enemies) {
    if (!e.alive || (huntedOnly && !e.hunted)) continue;
    for (const [dx, dy] of DIR_VECTORS) {
      const x = Math.floor(e.x) + dx, y = Math.floor(e.y) + dy;
      if (!inBounds(state, x, y)) continue;
      const cell = idxOf(state, x, y);
      if (danger[cell] === INF && dist[cell] > 0 && dist[cell] < nearest) {
        nearest = dist[cell]; target = cell;
      }
    }
  }
  return target;
}

/**
 * Idle drift.
 *
 * Two rules matter here. Holding a direction for a stretch of ticks stops the
 * bot vibrating in place (a fresh random direction every tick averages out to
 * standing still). And a drifting bot must never step into a tile that is on
 * fire or about to be: with the board full of bombs there is often nothing
 * useful to do, and a bot that wanders into its own dying blast looks — and
 * scores — like a bot with no idea what it is doing.
 */
function wander(
  state: GameState,
  bot: PlayerState,
  mem: BotMemory,
  danger: Int32Array,
  ticksPerTile: number,
  rand: () => number,
  out: Input,
): Input {
  const cx = Math.floor(bot.x);
  const cy = Math.floor(bot.y);
  const open = (d: number) => {
    const [dx, dy] = DIR_VECTORS[d];
    const nx = cx + dx;
    const ny = cy + dy;
    if (!inBounds(state, nx, ny)) return false;
    if (tileAt(state, nx, ny) !== Tile.EMPTY) return false;
    if (bombAt(state, nx, ny)) return false;
    const d0 = danger[idxOf(state, nx, ny)];
    return d0 === INF || d0 > ticksPerTile * 3;
  };
  if (mem.wanderTicks <= 0 || !open(mem.wanderDir)) {
    const choices = [0, 1, 2, 3].filter(open);
    if (!choices.length) {
      // Nowhere safe to go: hold position. We are only here because the tile
      // we are standing on is safe.
      mem.wanderTicks = 0;
      return out;
    }
    mem.wanderDir = choices[Math.floor(rand() * choices.length)];
    mem.wanderTicks = 8 + Math.floor(rand() * 14);
  }
  mem.wanderTicks--;
  const [dx, dy] = DIR_VECTORS[mem.wanderDir];
  out.mx = dx;
  out.my = dy;
  return out;
}

/**
 * Remote-bomb policy.
 *
 * A remote bomb has no fuse: it sits there until its owner presses the button.
 * That makes the trigger the bot's *only* way of getting the bomb slot back,
 * and a bot that only ever fires on a guaranteed kill ends up holding every
 * bomb it owns forever — out of ammo, hemmed in by its own permanent danger
 * zone, and looking, correctly, frozen. So the trigger is on a leash: fire it
 * the moment it kills someone, and otherwise fire it once patience runs out,
 * exactly as a fuse would have.
 *
 * Returns `'fire'` to press the button, `'turn'` when the bot must first look
 * away from a bomb (`punch` shares the action button and wins the tie in
 * `Sim.tryAction`, so pressing it while facing a bomb shoves that bomb down the
 * lane instead of setting it off), or `null` to do nothing.
 */
function considerDetonator(
  state: GameState,
  bot: PlayerState,
  mem: BotMemory,
  profile: BotProfile,
  here: number,
): 'fire' | 'turn' | null {
  if (!bot.remote) {
    mem.remoteHold = 0;
    return null;
  }
  const mine = state.bombs.filter((b) => b.ownerId === bot.id && b.remote && b.flight === 0);
  if (!mine.length) {
    mem.remoteHold = 0;
    return null;
  }
  mem.remoteHold++;

  // The button fires every remote bomb the bot owns, so the decision has to be
  // made against the union of their blasts, not one bomb at a time.
  const covered = new Set<number>();
  for (const b of mine) for (const idx of blastCells(state, b)) covered.add(idx);
  if (covered.has(here)) return null;

  const enemyIn = state.players.some(
    (p) =>
      p.alive &&
      p.id !== bot.id &&
      !(state.settings.mode === 'teams' && p.team === bot.team) &&
      covered.has(idxOf(state, Math.floor(p.x), Math.floor(p.y))),
  );

  // Tiers that understand gear hold the trigger a while, hunting for someone to
  // walk into it. The rest treat it as an ordinary fuse.
  const patience = profile.usesGear ? Math.round(3 * TICK_HZ) : BOMB_FUSE_TICKS;
  if (!enemyIn && mem.remoteHold < patience) return null;

  const [fx, fy] = DIR_VECTORS[bot.dir];
  if (bot.punch && bombAt(state, Math.floor(bot.x) + fx, Math.floor(bot.y) + fy)) return 'turn';
  mem.remoteHold = 0;
  return 'fire';
}

/**
 * Turns the bot to face something that is not a bomb, so the next press of the
 * action button detonates rather than punches.
 *
 * A wall is the best thing to face: `Sim` sets `dir` from the input before it
 * tries to move, so pushing into a hard block turns the bot on the spot without
 * taking it a step closer to anything. Open floor is the fallback.
 */
function faceAwayFromBomb(state: GameState, bot: PlayerState, out: Input): Input {
  const cx = Math.floor(bot.x);
  const cy = Math.floor(bot.y);
  const usable = (d: number): boolean => {
    if (d === bot.dir) return false;
    const [dx, dy] = DIR_VECTORS[d];
    return inBounds(state, cx + dx, cy + dy) && !bombAt(state, cx + dx, cy + dy);
  };
  const face = (d: number): Input => {
    const [dx, dy] = DIR_VECTORS[d];
    out.mx = dx;
    out.my = dy;
    return out;
  };
  for (let d = 0; d < 4; d++) {
    const [dx, dy] = DIR_VECTORS[d];
    if (usable(d) && tileAt(state, cx + dx, cy + dy) === Tile.HARD) return face(d);
  }
  for (let d = 0; d < 4; d++) if (usable(d)) return face(d);
  return out;
}

/**
 * Picks where to run to. Nearest-safe-cell is not good enough: a tile one step
 * beyond a blast tip gets swallowed the moment anything chains. Cells with a
 * dangerous neighbour are therefore penalised.
 */
function bestRefuge(state: GameState, dist: Int32Array, danger: Int32Array): number {
  let best = -1;
  let bestCost = Infinity;
  for (let idx = 0; idx < dist.length; idx++) {
    if (dist[idx] === INF || danger[idx] !== INF) continue;
    let exposed = 0;
    const x = idx % state.width;
    const y = (idx / state.width) | 0;
    for (let d = 0; d < 4; d++) {
      const [dx, dy] = DIR_VECTORS[d];
      const nx = x + dx;
      const ny = y + dy;
      if (!inBounds(state, nx, ny)) continue;
      if (danger[idxOf(state, nx, ny)] !== INF) exposed++;
    }
    const cost = dist[idx] + exposed * 2.5;
    if (cost < bestCost) {
      bestCost = cost;
      best = idx;
    }
  }
  return best;
}

/** True while the next few steps of a cached plan are still survivable. */
function planStillSafe(mem: BotMemory, danger: Int32Array, ticksPerTile: number, caution: number): boolean {
  const lookahead = Math.min(3, mem.plan.length);
  for (let i = 0; i < lookahead; i++) {
    const d0 = danger[mem.plan[i]];
    if (d0 === INF) continue;
    if (d0 <= (i + 2) * ticksPerTile + caution) return false;
  }
  return true;
}

function nearestWhere(dist: Int32Array, pred: (idx: number) => boolean): number {
  let best = -1;
  let bestD = INF;
  for (let idx = 0; idx < dist.length; idx++) {
    if (dist[idx] >= bestD) continue;
    if (!pred(idx)) continue;
    bestD = dist[idx];
    best = idx;
  }
  return best;
}

function pathTo(prev: Int32Array, from: number, to: number): number[] {
  const path: number[] = [];
  let cur = to;
  let guard = 0;
  while (cur !== from && cur !== -1 && guard++ < 4096) {
    path.push(cur);
    cur = prev[cur];
  }
  path.reverse();
  return path;
}

function followPlan(state: GameState, bot: PlayerState, mem: BotMemory, out: Input): Input {
  const cx = Math.floor(bot.x);
  const cy = Math.floor(bot.y);
  while (mem.plan.length && mem.plan[0] === idxOf(state, cx, cy)) mem.plan.shift();
  if (!mem.plan.length) return out;
  const next = mem.plan[0];
  const nx = next % state.width;
  const ny = (next / state.width) | 0;
  if (nx !== cx) out.mx = Math.sign(nx - cx);
  else if (ny !== cy) out.my = Math.sign(ny - cy);
  else mem.plan.shift();
  return out;
}

function adjacentSoft(state: GameState, idx: number): boolean {
  if (state.grid[idx] !== Tile.EMPTY) return false;
  const x = idx % state.width;
  const y = (idx / state.width) | 0;
  for (let d = 0; d < 4; d++) {
    const [dx, dy] = DIR_VECTORS[d];
    if (tileAt(state, x + dx, y + dy) === Tile.SOFT) return true;
  }
  return false;
}

interface Shot {
  value: number;
  /** True when at least one opponent has no way out of the blast. */
  lethal: boolean;
}

/**
 * Scores dropping a bomb on this tile.
 *
 * Soft blocks are worth a little; catching an opponent is worth a lot; and
 * catching an opponent who has nowhere to run is the whole game, so it is
 * scored separately and overrides the tier's aggression roll.
 */
/**
 * What the bot's *next* bomb will actually be, not what its stats say.
 *
 * A charged player's next bomb goes off at full range and pierces, and a bot
 * that plans with `bot.power` gets this wrong twice over. It undervalues the
 * shot, so it never holds the Overload for anything worth spending it on --
 * and, far worse, it computes its escape against a blast smaller than the one
 * it is about to create, and walks into it. Teaching the AI about the mechanic
 * starts here, because everything else reads these two numbers.
 *
 * A curse still wins, exactly as it does in the sim: TINY_FLAME beats a
 * charge, and a bot that planned otherwise would be planning around a bomb it
 * cannot place.
 */
function nextBomb(bot: PlayerState): { power: number; pierce: boolean } {
  if (bot.curse === Curse.TINY_FLAME) return { power: 1, pierce: bot.pierce };
  if (bot.charged) return { power: FULL_FIRE_POWER, pierce: true };
  return { power: bot.power, pierce: bot.pierce };
}

function evaluateBomb(
  state: GameState,
  bot: PlayerState,
  here: number,
  profile: BotProfile,
  ticksPerTile: number,
  danger: Int32Array,
): Shot {
  if (bombAt(state, here % state.width, (here / state.width) | 0)) return { value: 0, lethal: false };
  const next = nextBomb(bot);
  const cells = blastCells(state, { x: bot.x, y: bot.y, power: next.power, pierce: next.pierce });
  const covered = new Set(cells);
  let value = 0;
  let lethal = false;

  for (const idx of cells) {
    if (state.grid[idx] === Tile.SOFT) value += 1;
  }

  if (profile.huntRange > 0) {
    const hypothetical = Int32Array.from(danger);
    for (const idx of cells) {
      if (BOMB_FUSE_TICKS < hypothetical[idx]) hypothetical[idx] = BOMB_FUSE_TICKS;
    }
    for (const p of state.players) {
      if (!p.alive || p.id === bot.id) continue;
      if (state.settings.mode === 'teams' && p.team === bot.team) continue;
      const cell = idxOf(state, Math.floor(p.x), Math.floor(p.y));
      if (covered.has(cell)) {
        /*
         * A hit is not a kill when the target has points to spare.
         *
         * Without this the bot reads every trapped enemy as dead, commits to
         * a trade it does not win, and -- worse -- stops hunting once it
         * believes the job is done. Worth taking anyway: chipping a player
         * down is how they are eventually killed, so a non-lethal hit keeps
         * most of its value and loses the lethal bonus.
         */
        /*
         * Health is a percentage, so "will this kill them" is arithmetic.
         *
         * The bot reads the hits the target has left rather than a flag: at
         * one hit remaining a trapped enemy is still a kill and worth the
         * lethal bonus, and above that it is a chip. Reading it as a flag was
         * the first version and it made every healthy enemy look immortal.
         */
        const hits = Math.max(1, Math.round(state.settings.hitPoints ?? 1));
        const left = hits <= 1 ? 1 : Math.ceil(((p.hp ?? 100) / 100) * hits);
        const chip = left > 1;
        value += chip ? 3 : 6;
        if (!chip && !canEscapeFrom(state, p, cell, hypothetical, ticksPerTile)) {
          value += 20;
          lethal = true;
        }
      } else if (adjacentToBlast(state, cell, covered)) {
        // They are one step from walking into it; worth a speculative bomb.
        value += 2;
      }
    }
  }

  /*
   * Creatures in the blast.
   *
   * Scored above a soft block and below a player kill: emptying a room is the
   * objective in most Expedition rooms, so a bomb that catches two drifters is
   * the best move on the board -- but a creature cannot shoot back, so it is
   * never the emergency a trapped player is.
   *
   * No escape check, unlike players. A creature has no notion of safety and
   * does not run from fire, so "can it get out" is not a question about it --
   * whether it is standing there when the fuse ends is down to its movement
   * rule, and the bot is right to take the shot either way.
   *
   * Outside `profile.huntRange` guard on purpose: even a Rookie should clear a
   * room it is standing in, and the gate above is about hunting *players*.
   */
  for (const e of state.enemies) {
    if (!e.alive || (state.settings.roomGoal === RoomGoal.HUNT && !e.hunted)) continue;
    const cell = idxOf(state, Math.floor(e.x), Math.floor(e.y));
    if (covered.has(cell)) {
      value += e.hunted ? 10 : e.hp > 1 ? 3 : 5;
      if (e.hp <= 1) lethal = true;
    } else if (adjacentToBlast(state, cell, covered)) {
      value += 2;
    }
  }
  return { value, lethal };
}

function adjacentToBlast(state: GameState, cell: number, covered: Set<number>): boolean {
  const x = cell % state.width;
  const y = (cell / state.width) | 0;
  for (let d = 0; d < 4; d++) {
    const [dx, dy] = DIR_VECTORS[d];
    if (!inBounds(state, x + dx, y + dy)) continue;
    if (covered.has(idxOf(state, x + dx, y + dy))) return true;
  }
  return false;
}

/** Would this player be able to reach safety before the blast lands? */
function canEscapeFrom(
  state: GameState,
  player: PlayerState,
  from: number,
  danger: Int32Array,
  ticksPerTile: number,
): boolean {
  const theirTicksPerTile = Math.max(1, Math.round(TICK_HZ / (3.6 + player.speedLevel * 0.55)));
  const search = bfs(state, from, danger, theirTicksPerTile, 0, false, false);
  for (let idx = 0; idx < search.dist.length; idx++) {
    if (idx === from || search.dist[idx] === INF) continue;
    if (danger[idx] !== INF) continue;
    if (search.dist[idx] * theirTicksPerTile < BOMB_FUSE_TICKS) return true;
  }
  void ticksPerTile;
  return false;
}

/** Simulates our own bomb and checks a safe tile is still reachable in time. */
function hasEscape(
  state: GameState,
  bot: PlayerState,
  here: number,
  danger: Int32Array,
  ticksPerTile: number,
  profile: BotProfile,
): boolean {
  const hypothetical = Int32Array.from(danger);
  const fuse = BOMB_FUSE_TICKS;
  // The blast it is about to make, which is bigger than its stats when an
  // Overload is armed. Planning the escape against the smaller one is how a
  // comeback mechanic would turn into a suicide mechanic.
  const next = nextBomb(bot);
  for (const idx of blastCells(state, { x: bot.x, y: bot.y, power: next.power, pierce: next.pierce })) {
    if (fuse < hypothetical[idx]) hypothetical[idx] = fuse;
  }
  const escape = bfs(state, here, hypothetical, ticksPerTile, profile.caution, false);
  // Real travel is slower than dist * ticksPerTile: the bot has to finish
  // crossing its current tile and re-align at every corner. Pad the estimate
  // rather than discovering the shortfall at detonation time.
  const perTile = ticksPerTile * 1.3;
  for (let idx = 0; idx < escape.dist.length; idx++) {
    if (idx === here || escape.dist[idx] === INF) continue;
    if (hypothetical[idx] !== INF) continue;
    if (escape.dist[idx] * perTile + ticksPerTile + profile.caution < fuse) return true;
  }
  return false;
}
